import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { registrationsApi } from '../api/registrations';
import type { SaveDraftData, UpdateDetailsData, UpdateMembersData, UpdateVillaPreferenceData } from '../api/registrations';
import { useRegistrationStore } from '../store/registrationStore';

export const registrationKeys = {
  all: ['registrations'] as const,
  mine: () => [...registrationKeys.all, 'mine'] as const,
};

/** Fetch current user's registration for the active reunion */
export function useMyRegistration() {
  const hydrateFromRegistration = useRegistrationStore((s) => s.hydrateFromRegistration);

  return useQuery({
    queryKey: registrationKeys.mine(),
    queryFn: async () => {
      const response = await registrationsApi.getMine();
      if (response.registration) {
        hydrateFromRegistration(response.registration);
      }
      return response.registration;
    },
  });
}

/** Save draft registration */
export function useSaveDraft() {
  const queryClient = useQueryClient();
  const setLastSavedAt = useRegistrationStore((s) => s.setLastSavedAt);
  const setIsSaving = useRegistrationStore((s) => s.setIsSaving);
  const setRegistration = useRegistrationStore((s) => s.setRegistration);

  return useMutation({
    mutationFn: (data: SaveDraftData) => registrationsApi.saveDraft(data),
    onMutate: () => setIsSaving(true),
    onSuccess: (response) => {
      if (response.registration) {
        setRegistration(response.registration);
        setLastSavedAt(new Date().toISOString());
      }
      queryClient.invalidateQueries({ queryKey: registrationKeys.mine() });
    },
    onSettled: () => setIsSaving(false),
  });
}

/** Submit registration (draft -> pending) */
export function useSubmitRegistration() {
  const queryClient = useQueryClient();
  const setRegistration = useRegistrationStore((s) => s.setRegistration);

  return useMutation({
    mutationFn: (registrationId: string) => registrationsApi.submit(registrationId),
    onSuccess: (response) => {
      if (response.registration) {
        setRegistration(response.registration);
      }
      queryClient.invalidateQueries({ queryKey: registrationKeys.mine() });
    },
  });
}

/** Update registration details (phone, familyConnection) */
export function useUpdateRegistrationDetails() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & UpdateDetailsData) =>
      registrationsApi.updateDetails(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registrationKeys.mine() });
    },
  });
}

/** Update family members array */
export function useUpdateFamilyMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & UpdateMembersData) =>
      registrationsApi.updateMembers(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registrationKeys.mine() });
    },
  });
}

/** Update villa preference */
export function useUpdateVillaPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & UpdateVillaPreferenceData) =>
      registrationsApi.updateVillaPreference(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registrationKeys.mine() });
    },
  });
}
